"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import { Search, Add, FilterList, Refresh } from "@mui/icons-material";
import DisciplinaryCaseTable from "./DisciplinaryCaseTable";
import { getDisciplinaryCases } from "../../api/disciplinaryService";
import { getAllEmployees } from "../../api/LeaveService";
import { IonContent, IonPage } from "@ionic/react";
import styles from "./EmployerDisciplinaryView.module.css";

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-tabpanel-${index}`}
      aria-labelledby={`case-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function EmployerDisciplinaryView() {
  const history = useHistory();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [disciplinaryCases, setDisciplinaryCases] = useState([]);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);
  const [employees, setEmployees] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeData = await getAllEmployees();
        setEmployees(employeeData);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        setError("Failed to load employee data. Please try again later.");
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchDisciplinaryCases = async () => {
      try {
        setLoading(true);
        const data = await getDisciplinaryCases();
        setDisciplinaryCases(data);
        setSuccess("Data loaded successfully");
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError("Failed to load disciplinary cases");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDisciplinaryCases();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      // Simulate refresh
      setLoading(false);
      setSuccess("Data refreshed successfully");
      setTimeout(() => setSuccess(null), 3000);
    }, 1000);
  };

  const filterCases = (cases: any[]) => {
    return cases?.filter((item) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        item?.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.caseNumber?.toString().includes(searchTerm) ||
        employees.some(
          (emp) =>
            (emp?.user_id === item?.user_id || item?.employeeId) &&
            (emp.uname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              emp.employeeId?.toString().includes(searchTerm))
        );

      // Status filter
      const matchesStatus =
        statusFilter === "all" || item?.status === statusFilter;

      // Department filter
      const matchesDepartment =
        departmentFilter === "all" || item?.department === departmentFilter;

      return matchesSearch && matchesStatus && matchesDepartment;
    });
  };

  const getPendingCount = () => {
    return disciplinaryCases?.filter(
      (item: any) =>
        item.status === "response_submitted" || item.status === "pending"
    ).length;
  };

  const getTabCases = () => {
    switch (tabValue) {
      case 0: // Pending Review
        return disciplinaryCases?.filter(
          (item: any) =>
            item?.status === "response_submitted" || item?.status === "pending"
        );
      case 1: // Active Cases
        return disciplinaryCases?.filter(
          (item: any) => item.status !== "closed"
        );
      case 2: // All Cases
        return disciplinaryCases;
      default:
        return [];
    }
  };

  const filteredCases = filterCases(getTabCases());
  const pendingCount = getPendingCount();

  // Pagination
  const pageCount = Math.ceil(filteredCases?.length / rowsPerPage);
  const paginatedCases = filteredCases?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <IonPage style={{ overflow: "scroll" }} className="pushDown">
      <IonContent>
        <div className={styles.container}>
          <div className={styles.header}>
            <Typography
              variant="h4"
              component="h5"
              className={styles.sectionTitle}
              fontWeight="600"
            >
              Disciplinary Cases
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => history.push("/issueDiscipline")}
              className={styles.newButton}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                },
              }}
            >
              Issue New Query
            </Button>
          </div>

          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} className={styles.statsContainer}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={styles.statCard}
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent
                  className={styles.statContent}
                  sx={{ textAlign: "center" }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="700"
                    color="primary.main"
                  >
                    {pendingCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={styles.statCard}
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.info.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent
                  className={styles.statContent}
                  sx={{ textAlign: "center" }}
                >
                  <Typography variant="h3" fontWeight="700" color="info.main">
                    {disciplinaryCases?.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Queries Issued
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={styles.statCard}
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.warning.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent
                  className={styles.statContent}
                  sx={{ textAlign: "center" }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="700"
                    color="warning.main"
                  >
                    {
                      disciplinaryCases?.filter(
                        (item: any) => item.status === "penalty_applied"
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Penalties Applied
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={styles.statCard}
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.success.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent
                  className={styles.statContent}
                  sx={{ textAlign: "center" }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="700"
                    color="success.main"
                  >
                    {
                      disciplinaryCases?.filter(
                        (item: any) => item.status === "closed"
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Closed Cases
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card
            className={styles.mainCard}
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardHeader
              title="Manage Disciplinary Cases"
              subheader="Review and manage employee disciplinary cases"
              className={styles.cardHeader}
              action={
                <IconButton
                  onClick={handleRefresh}
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : <Refresh />}
                </IconButton>
              }
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                background: alpha(theme.palette.background.default, 0.5),
              }}
            />
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="disciplinary cases tabs"
                  variant="fullWidth"
                >
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        Pending Review
                        {pendingCount > 0 && (
                          <Chip
                            label={pendingCount}
                            color="error"
                            size="small"
                            sx={{ minWidth: 24, height: 24 }}
                          />
                        )}
                      </Box>
                    }
                    id="case-tab-0"
                    aria-controls="case-tabpanel-0"
                    sx={{ fontWeight: 600, textTransform: "none" }}
                  />
                  <Tab
                    label="Active Cases"
                    id="case-tab-1"
                    aria-controls="case-tabpanel-1"
                    sx={{ fontWeight: 600, textTransform: "none" }}
                  />
                  <Tab
                    label="All Cases"
                    id="case-tab-2"
                    aria-controls="case-tabpanel-2"
                    sx={{ fontWeight: 600, textTransform: "none" }}
                  />
                </Tabs>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  my: 3,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  placeholder="Search by employee name, ID or case number"
                  variant="outlined"
                  size="small"
                  className={styles.searchField}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 },
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />

                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  Filters
                </Button>
              </Box>

              {showFilters && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5
                    ),
                    flexWrap: "wrap",
                  }}
                >
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(e.target.value as string)
                      }
                      label="Status"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="query_issued">Query Issued</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="response_submitted">
                        Response Submitted
                      </MenuItem>
                      <MenuItem value="penalty_applied">
                        Penalty Applied
                      </MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    <InputLabel id="department-filter-label">
                      Department
                    </InputLabel>
                    <Select
                      labelId="department-filter-label"
                      id="department-filter"
                      value={departmentFilter}
                      onChange={(e) =>
                        setDepartmentFilter(e.target.value as string)
                      }
                      label="Department"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">All Departments</MenuItem>
                      <MenuItem value="engineering">Engineering</MenuItem>
                      <MenuItem value="marketing">Marketing</MenuItem>
                      <MenuItem value="finance">Finance</MenuItem>
                      <MenuItem value="hr">Human Resources</MenuItem>
                      <MenuItem value="operations">Operations</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <TabPanel value={tabValue} index={0}>
                <DisciplinaryCaseTable
                  cases={paginatedCases}
                  loading={loading}
                  error={error}
                  isEmployeeView={false}
                  employees={employees}
                  setEmployees={setEmployees}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <DisciplinaryCaseTable
                  cases={paginatedCases}
                  loading={loading}
                  error={error}
                  isEmployeeView={false}
                  employees={employees}
                  setEmployees={setEmployees}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <DisciplinaryCaseTable
                  cases={paginatedCases}
                  loading={loading}
                  error={error}
                  isEmployeeView={false}
                  employees={employees}
                  setEmployees={setEmployees}
                />
              </TabPanel>

              {filteredCases?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 3,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    <InputLabel id="rows-per-page-label">
                      Rows per page
                    </InputLabel>
                    <Select
                      labelId="rows-per-page-label"
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      label="Rows per page"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography variant="body2" color="text.secondary">
                    Showing {(page - 1) * rowsPerPage + 1} to{" "}
                    {Math.min(page * rowsPerPage, filteredCases.length)} of{" "}
                    {filteredCases.length} entries
                  </Typography>

                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handleChangePage}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </div>
      </IonContent>
    </IonPage>
  );
}
